// Onboarding: should feel like a hype mint and take under 90 seconds (spec section 5).
// Splash -> name/handle -> PFP -> look (body type) -> origin roll -> summary.

import { LoadingSponsor } from "../ads/AdSlots";
import { track } from "../../net/telemetry";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { TUNABLES } from "@degen/shared/config/tunables";
import { PRESET_BIOS, CURRENCY } from "@degen/shared/config/content";
import { COLLECTION_NAME, LAYER_ORDER, traitLabel, traitProbability, tokenNumber } from "@degen/sim/pfp/traits";
import { avatarFromPfp, BODY_TYPES, type BodyType } from "@degen/sim/avatar/traits";
import { BIO_MAX, cleanHandle, outfitSeed, pfpGrid, randomIdentity, rollOrigin, validHandle, validName, type PfpCandidate } from "@degen/sim/newGame";
import { createPlayer, type NewPlayerInput, type Origin } from "@degen/sim/player";
import { rankIndex, rankName } from "@degen/sim/rank";
import { PfpImage } from "../PfpImage";
import { SpriteWalker } from "../SpriteWalker";
import { compact, wusd } from "../format";

type Step = "splash" | "identity" | "pfp" | "look" | "origin" | "summary";
const STEPS: Step[] = ["identity", "pfp", "look", "origin", "summary"];

export interface OnboardingProps {
  seed: number;
  hasSave: boolean;
  onContinue(): void;
  onComplete(seed: number, input: NewPlayerInput): void;
}

const ORIGIN_TEXT: Record<Origin, { title: string; flavor: string }> = {
  novice: { title: "Novice", flavor: "You have a phone, Wi-Fi, and a dream." },
  kol: { title: "KOL", flavor: "They listen to you. For now." },
};

const BODY_LABEL: Record<BodyType, string> = { male: "Male", female: "Female", anon: "Anon" };

const vibrate = (p: number | number[]) => {
  try {
    navigator.vibrate?.(p);
  } catch {
    /* unsupported */
  }
};

export function Onboarding({ seed, hasSave, onContinue, onComplete }: OnboardingProps) {
  const [step, setStepRaw] = useState<Step>("splash");
  const setStep = (s: Step) => {
    track("onboarding_step", { step: s });
    setStepRaw(s);
  };
  const first = useMemo(() => randomIdentity(seed, 0), [seed]);
  const [idN, setIdN] = useState(0);
  const [name, setName] = useState(first.name);
  const [handle, setHandle] = useState(first.handle);
  const [bio, setBio] = useState(first.bio);
  const [roll, setRoll] = useState(0);
  const grid = useMemo(() => pfpGrid(seed, roll), [seed, roll]);
  const [picked, setPicked] = useState<PfpCandidate | null>(null);
  const [body, setBody] = useState<BodyType>("anon");
  const [fitN, setFitN] = useState(0);
  const [origin, setOrigin] = useState<Origin | null>(null);

  const pfp = picked ?? grid[0];
  const avatar = useMemo(() => avatarFromPfp(pfp.traits, body, outfitSeed(seed, fitN)), [pfp, body, fitN, seed]);

  const input = (): NewPlayerInput => ({
    name: name.trim(),
    handle,
    bio: bio.trim(),
    pfp: { seed: pfp.seed, traits: pfp.traits, rarity: pfp.rarity.tier },
    avatar,
    origin: origin ?? "novice",
  });

  if (step === "splash") {
    return <Splash hasSave={hasSave} onContinue={onContinue} onNew={() => setStep("identity")} />;
  }

  return (
    <div class="onb">
      <div class="onb-progress" aria-hidden="true">
        {STEPS.map((s) => (
          <span key={s} class={STEPS.indexOf(s) <= STEPS.indexOf(step) ? "on" : ""} />
        ))}
      </div>
      {step === "identity" && (
        <IdentityStep
          {...{ name, handle, bio, pfp }}
          setName={setName}
          setHandle={setHandle}
          setBio={setBio}
          onRandom={() => {
            const r = randomIdentity(seed, idN + 1);
            setIdN(idN + 1);
            setName(r.name);
            setHandle(r.handle);
          }}
          onNext={() => setStep("pfp")}
        />
      )}
      {step === "pfp" && (
        <PfpStep
          grid={grid}
          roll={roll}
          picked={picked}
          onPick={setPicked}
          onReroll={() => {
            setRoll(roll + 1);
            setPicked(null);
            vibrate(20);
          }}
          onNext={() => setStep("look")}
        />
      )}
      {step === "look" && (
        <LookStep pfp={pfp} body={body} setBody={setBody} fitSeed={(b) => avatarFromPfp(pfp.traits, b, outfitSeed(seed, fitN))} onShuffle={() => setFitN(fitN + 1)} onNext={() => setStep("origin")} />
      )}
      {step === "origin" && (
        <OriginStep
          result={rollOrigin(seed)}
          onDone={(o) => {
            setOrigin(o);
            setStep("summary");
          }}
        />
      )}
      {step === "summary" && origin && <Summary input={input()} onStart={() => onComplete(seed, input())} />}
    </div>
  );
}

// ---------------------------------------------------------------- splash

function CandleBackground() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current!;
    const ctx = c.getContext("2d")!;
    const W = 48;
    const candles: { o: number; c: number; h: number; l: number }[] = [];
    let price = 50;
    const push = () => {
      const o = price;
      price = Math.max(10, Math.min(90, price + (Math.random() - 0.45) * 9));
      candles.push({ o, c: price, h: Math.max(o, price) + Math.random() * 4, l: Math.min(o, price) - Math.random() * 4 });
      if (candles.length > W) candles.shift();
    };
    for (let i = 0; i < W; i++) push();
    let raf = 0;
    let last = 0;
    const draw = (t: number) => {
      raf = requestAnimationFrame(draw);
      if (t - last < 180) return;
      last = t;
      push();
      const w = (c.width = c.clientWidth);
      const h = (c.height = c.clientHeight);
      const cw = w / W;
      const y = (v: number) => h - (v / 100) * h;
      candles.forEach((k, i) => {
        ctx.fillStyle = k.c >= k.o ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)";
        ctx.fillRect(i * cw + cw / 2 - 1, y(k.h), 2, y(k.l) - y(k.h));
        ctx.fillRect(i * cw + cw * 0.2, y(Math.max(k.o, k.c)), cw * 0.6, Math.max(2, Math.abs(y(k.o) - y(k.c))));
      });
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={ref} class="onb-candles" aria-hidden="true" />;
}

function Splash({ hasSave, onContinue, onNew }: { hasSave: boolean; onContinue(): void; onNew(): void }) {
  const [confirm, setConfirm] = useState(false);
  return (
    <div class="onb splash">
      <CandleBackground />
      <div class="splash-inner">
        <h1 class="logo">
          DEGEN<span>WORLD</span>
        </h1>
        <p class="muted">Start broke or start famous. Grind, trade, launch, post. Try not to get rekt.</p>
        <LoadingSponsor />
        {!hasSave && (
          <button class="btn primary big" onClick={onNew}>
            Enter the trenches
          </button>
        )}
        {hasSave && !confirm && (
          <>
            <button class="btn primary big" onClick={onContinue}>
              Continue
            </button>
            <button class="btn ghost" onClick={() => setConfirm(true)}>
              New game
            </button>
          </>
        )}
        {hasSave && confirm && (
          <div class="confirm">
            <p>Start a new life? Your current save will be overwritten.</p>
            <button class="btn danger" onClick={onNew}>
              Yes, new life
            </button>
            <button class="btn ghost" onClick={() => setConfirm(false)}>
              Keep my save
            </button>
          </div>
        )}
        <p class="fine">Fictional economy. Nothing here is real money or financial advice.</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- identity

function IdentityStep(p: {
  name: string;
  handle: string;
  bio: string;
  pfp: PfpCandidate;
  setName(v: string): void;
  setHandle(v: string): void;
  setBio(v: string): void;
  onRandom(): void;
  onNext(): void;
}) {
  const nameErr = validName(p.name);
  const handleErr = validHandle(p.handle);
  const custom = !PRESET_BIOS.includes(p.bio);
  return (
    <div class="onb-step">
      <h2>Who are you, anon?</h2>
      <label class="field">
        <span>Display name</span>
        <input value={p.name} maxLength={40} onInput={(e) => p.setName(e.currentTarget.value)} />
        {nameErr && <em>{nameErr}</em>}
      </label>
      <label class="field">
        <span>@handle</span>
        <div class="handle-input">
          <b>@</b>
          <input
            value={p.handle}
            autoCapitalize="off"
            autoCorrect="off"
            spellcheck={false}
            onInput={(e) => {
              const v = cleanHandle(e.currentTarget.value);
              e.currentTarget.value = v;
              p.setHandle(v);
            }}
          />
        </div>
        {handleErr && <em>{handleErr}</em>}
      </label>
      <button class="btn ghost" onClick={p.onRandom}>
        🎲 Randomize
      </button>
      <div class="field">
        <span>Bio</span>
        <div class="chips">
          {PRESET_BIOS.map((b) => (
            <button key={b} class={`chip ${p.bio === b ? "on" : ""}`} onClick={() => p.setBio(b)}>
              {b}
            </button>
          ))}
        </div>
        <input
          placeholder="…or write your own"
          maxLength={BIO_MAX}
          value={custom ? p.bio : ""}
          onInput={(e) => p.setBio(e.currentTarget.value)}
        />
      </div>
      <div class="chirp-preview" aria-label="Post preview">
        <PfpImage seed={p.pfp.seed} traits={p.pfp.traits} size={44} />
        <div>
          <div>
            <b>{p.name.trim() || "anon"}</b> <span class="muted">@{p.handle || "anon"} · 1m</span>
          </div>
          <div>gm</div>
          <div class="muted small">💬 3 🔁 1 ❤️ 12</div>
        </div>
      </div>
      <button class="btn primary big" disabled={!!nameErr || !!handleErr} onClick={p.onNext}>
        Next: mint your PFP
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- pfp

const LAYER_LABEL: Record<string, string> = { background: "BG", skin: "Skin", eyes: "Eyes", mouth: "Mouth", headwear: "Hat", accessory: "Extra" };

const TIER_CLASS: Record<string, string> = { Common: "t-common", Uncommon: "t-uncommon", Rare: "t-rare", Epic: "t-epic", Legendary: "t-legendary" };

function PfpStep(p: { grid: PfpCandidate[]; roll: number; picked: PfpCandidate | null; onPick(c: PfpCandidate): void; onReroll(): void; onNext(): void }) {
  const left = TUNABLES.pfp.rerolls - p.roll;
  return (
    <div class="onb-step">
      <h2>Pick your {COLLECTION_NAME.replace(/s$/, "")}</h2>
      <p class="muted">This is your face on Chirp and in town. Rare or better gets a small follower boost.</p>
      <div class="pfp-grid" key={p.roll}>
        {p.grid.map((c, i) => (
          <button
            key={c.seed}
            class={`pfp-card mint-in ${p.picked?.seed === c.seed ? "on" : ""}`}
            style={{ animationDelay: `${i * 70}ms` }}
            onClick={() => p.onPick(c)}
          >
            <PfpImage seed={c.seed} traits={c.traits} size={128} />
            <div class="pfp-meta">
              <span class="num">#{tokenNumber(c.seed)}</span>
              <span class={`tier ${TIER_CLASS[c.rarity.tier]}`}>{c.rarity.tier}</span>
            </div>
            <div class="muted small">Rarity rank {c.rarity.rank.toLocaleString()}</div>
            <ul class="traits">
              {LAYER_ORDER.map((l) => (
                <li key={l}>
                  <span>
                    <span class="muted">{LAYER_LABEL[l]}</span> {traitLabel(c.traits[l])}
                  </span>
                  <span class="num muted">{(traitProbability(l, c.traits[l]) * 100).toFixed(0)}%</span>
                </li>
              ))}
            </ul>
          </button>
        ))}
      </div>
      <div class="row">
        <button class="btn ghost" disabled={left <= 0} onClick={p.onReroll}>
          🔄 Reroll ({left} left)
        </button>
        <button class="btn primary" disabled={!p.picked} onClick={p.onNext}>
          {p.picked ? "Next: your look" : "Tap a PFP"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- look

function LookStep(p: {
  pfp: PfpCandidate;
  body: BodyType;
  setBody(b: BodyType): void;
  fitSeed(b: BodyType): ReturnType<typeof avatarFromPfp>;
  onShuffle(): void;
  onNext(): void;
}) {
  return (
    <div class="onb-step">
      <h2>How do you show up in town?</h2>
      <p class="muted">Your character matches your PFP. Anon keeps the hood up and the face hidden. Very on brand.</p>
      <div class="look-grid">
        {BODY_TYPES.map((b) => (
          <button key={b} class={`look-card ${p.body === b ? "on" : ""}`} onClick={() => p.setBody(b)}>
            <SpriteWalker av={p.fitSeed(b)} scale={3} walking={p.body === b} dir={p.body === b ? null : 0} />
            <span>{BODY_LABEL[b]}</span>
          </button>
        ))}
      </div>
      <div class="row">
        <button class="btn ghost" onClick={p.onShuffle}>
          👕 Shuffle fit
        </button>
        <button class="btn primary" onClick={p.onNext}>
          Next: roll your origin
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- origin

function OriginStep({ result, onDone }: { result: Origin; onDone(o: Origin): void }) {
  const [choose, setChoose] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [shown, setShown] = useState<Origin>("novice");
  const [landed, setLanded] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const spin = () => {
    setSpinning(true);
    let delay = 55;
    let elapsed = 0;
    let cur: Origin = "novice";
    const tickReel = () => {
      cur = cur === "novice" ? "kol" : "novice";
      elapsed += delay;
      delay *= 1.13;
      // Land on the result once ~3s have passed.
      if (elapsed > 2600 && cur === result) {
        setShown(cur);
        setLanded(true);
        vibrate(result === "kol" ? [60, 40, 120] : 80);
        timer.current = setTimeout(() => onDone(result), 1100);
        return;
      }
      setShown(cur);
      vibrate(8);
      timer.current = setTimeout(tickReel, delay);
    };
    tickReel();
  };

  return (
    <div class="onb-step center">
      <h2>Roll your origin</h2>
      <p class="muted">75% start as a broke Novice. 25% wake up a KOL.</p>
      <div class={`reel ${spinning ? "spinning" : ""} ${landed ? `landed ${shown}` : ""}`} aria-live="polite">
        <div class="reel-label">{spinning ? ORIGIN_TEXT[shown].title : "?"}</div>
        {landed && <div class="reel-flavor">{ORIGIN_TEXT[shown].flavor}</div>}
      </div>
      {!spinning && !choose && (
        <button class="btn primary big" onClick={spin}>
          🎰 Roll
        </button>
      )}
      {!spinning && (
        <label class="toggle">
          <input type="checkbox" checked={choose} onChange={(e) => setChoose(e.currentTarget.checked)} />
          Let me choose my origin
        </label>
      )}
      {!spinning && choose && (
        <div class="row">
          <button class="btn" onClick={() => onDone("novice")}>
            Novice
          </button>
          <button class="btn" onClick={() => onDone("kol")}>
            KOL
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- summary

function Summary({ input, onStart }: { input: NewPlayerInput; onStart(): void }) {
  const p = createPlayer(input);
  const rank = rankName(rankIndex(p.cash, p.followers));
  const flex = TUNABLES.pfp.followerBonus[input.pfp.rarity] > 0;
  return (
    <div class="onb-step center">
      <div class={`summary-card ${input.origin}`}>
        <div class="summary-top">
          <PfpImage seed={input.pfp.seed} traits={input.pfp.traits} size={112} />
          <SpriteWalker av={input.avatar} scale={3} />
        </div>
        <h2>{input.name}</h2>
        <p class="muted">@{input.handle}</p>
        <p class="small">{input.bio}</p>
        <div class="badges">
          <span class={`origin-badge ${input.origin}`}>{ORIGIN_TEXT[input.origin].title}</span>
          <span class={`tier ${TIER_CLASS[input.pfp.rarity]}`}>
            #{tokenNumber(input.pfp.seed)} · {input.pfp.rarity}
          </span>
          {flex && <span class="badge">PFP flex</span>}
        </div>
        <div class="stats">
          <div>
            <span class="muted small">{CURRENCY.main.code}</span>
            <b class="num">{wusd(p.cash)}</b>
          </div>
          <div>
            <span class="muted small">Followers</span>
            <b class="num">{compact(p.followers)}</b>
          </div>
          <div>
            <span class="muted small">Trust</span>
            <b class="num">{p.trust}</b>
          </div>
          <div>
            <span class="muted small">Rank</span>
            <b>{rank}</b>
          </div>
        </div>
        <p class="flavor">{ORIGIN_TEXT[input.origin].flavor}</p>
      </div>
      <button class="btn primary big" onClick={onStart}>
        Start Day 1
      </button>
    </div>
  );
}
